import type { ReactElement } from "react";
import { FilesIcon, SendHorizontalIcon, XIcon } from "lucide-react";

import type { QueuedComposerMessage } from "../../queuedMessageStore";

/**
 * The queued follow-up strip above the composer: one row per message in send
 * order, each with its snapshot summary and the two row actions, steer (send
 * it now, mid-turn) and remove.
 */
export function QueuedComposerMessages(props: {
  readonly queuedMessages: ReadonlyArray<QueuedComposerMessage>;
  readonly onSteerQueuedMessage: (id: string) => void;
  readonly onRemoveQueuedMessage: (id: string) => void;
}): ReactElement {
  if (props.queuedMessages.length === 0) {
    return <></>;
  }
  return (
    <div data-testid="queued-composer-messages" className="mx-auto mb-1.5 w-full min-w-0 max-w-3xl">
      {props.queuedMessages.map((message) => (
        <div
          key={message.id}
          data-testid={`queued-composer-message-${message.id}`}
          className="flex items-center gap-2 rounded-sm border bg-muted/40 px-2.5 py-1.5"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">
              {message.prompt.length > 0 ? message.prompt : "(attachments only)"}
            </p>
            {message.files.length > 0 || message.images.length > 0 ? (
              <p className="text-muted-foreground text-xs">
                <FilesIcon className="mr-1 inline size-3" />
                {message.files.length} file{message.files.length === 1 ? "" : "s"}
                {message.images.length > 0 ? `, ${message.images.length} image(s)` : ""}
              </p>
            ) : null}
            {message.holdUntilUserAction ? (
              <p className="text-muted-foreground text-xs">Waiting for Send now.</p>
            ) : null}
          </div>
          <button
            type="button"
            data-testid={`queued-composer-steer-${message.id}`}
            aria-label="Send this queued message now"
            onClick={() => props.onSteerQueuedMessage(message.id)}
            className="flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <SendHorizontalIcon className="size-3.5" />
          </button>
          <button
            type="button"
            data-testid={`queued-composer-remove-${message.id}`}
            aria-label="Remove this queued message"
            onClick={() => props.onRemoveQueuedMessage(message.id)}
            className="flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
